import {contextRangesForMarkers} from './context-ranges-for-markers'
import {Deque} from './deque'
import {scheduleLowPriorityUpdate, type CancelHandle} from '@github-ui/schedule-low-priority-update'
import type {ProgressiveDiffsSlice} from '../stores/slices/progressive-diffs'
import type {DiffEntriesSlice} from '../stores/slices/diff-entries'
import type {DiffSummariesSlice} from '../stores/slices/diff-summaries'

export const makeProgressiveDiffLoader = ({
  get,
  initialLoadedPathDigests,
}: {
  get: () => ProgressiveDiffsSlice & DiffEntriesSlice & DiffSummariesSlice
  initialLoadedPathDigests: Set<string>
}) => {
  // Track which digests to load. Digests are de-queued from the front of the line
  // Important digests are placed in the front of the line
  // All other digests queue at the end of the line
  const digestsToLoad = new Deque<string>()

  // All digests which have been loaded by this loader
  const digestsLoaded = new Set(initialLoadedPathDigests)

  // All digests which are in flight
  const digestsLoading = new Set<string>()

  // For most operations, the loader will continually fetch digests until all are loaded
  // exceptions: virtual and single file views
  let autoLoadAllDiffs = false

  let queueRunning = false

  // By default, only fetch 4 files per network request
  // Exception: single file and virtual mode
  let lengthOfItemsToLoad = 4

  // Track the current scheduled update so it can be cancelled for debouncing
  let pendingUpdate: CancelHandle | null = null

  const debouncedProcessQueue = () => {
    // Cancel any pending scheduled update before scheduling a new one
    pendingUpdate?.cancel()
    pendingUpdate = scheduleLowPriorityUpdate(processQueue, {timeout: 50})
  }

  // A loading loop which continually loads files until all files are loaded
  const processQueue = async () => {
    const {entriesMap, entriesOrder, selectedPathDigest} = get()

    if (entriesOrder.length === digestsLoaded.size) {
      // 🎉 all diffs loaded, stop queue
      queueRunning = false
      return
    }

    // Find the next files to load based on the current selected digest
    // All files after the digest will be auto loaded
    if (autoLoadAllDiffs && digestsToLoad.size === 0) {
      const nextPathDigestToLoad = selectedPathDigest
        ? entriesOrder.indexOf(selectedPathDigest)
        : entriesOrder.findIndex((pathDigest: string) => !digestsLoaded.has(pathDigest))

      if (nextPathDigestToLoad > 0) {
        for (let i = nextPathDigestToLoad; i < entriesOrder.length; i++) {
          const pathDigest = entriesOrder[i]
          if (!pathDigest) break

          if (!digestsLoaded.has(pathDigest) && !digestsLoading.has(pathDigest)) {
            if (entriesMap[pathDigest]?.loadSolo) {
              digestsToLoad.pushFront(pathDigest)
            } else {
              digestsToLoad.pushBack(pathDigest)
            }
          }

          if (digestsToLoad.size >= lengthOfItemsToLoad) {
            break
          }
        }
      }
    }

    // What paths to load in this loop. Today the API only accepts paths, but it should actually accept a path digest
    const paths: string[] = []

    // load up the next batch of diffs to load. Stop when:
    // 1. no more digests in the queue
    // 2. diff chunk size it full
    while (digestsToLoad.size > 0 && paths.length < lengthOfItemsToLoad) {
      const pathDigest = digestsToLoad.popFront()
      if (!pathDigest || digestsLoaded.has(pathDigest) || digestsLoading.has(pathDigest)) {
        continue
      }
      const path = entriesMap[pathDigest]?.path
      if (path) {
        paths.push(path)
        digestsLoading.add(pathDigest)
      }
    }

    // No paths found to load, loop again until more files are added
    if (!paths.length) {
      return debouncedProcessQueue()
    }

    const contextRanges = contextRangesForMarkers({
      paths: new Set(paths),
      diffSummaries: get().diffSummaries,
    })

    // progressive diff entries are updated in the other store to avoid two zustand updates for perf
    const loadedEntries = await get().diffEntriesActions.loadDiffEntries({
      paths,
      contextRanges,
    })

    for (const entry of loadedEntries) {
      digestsLoading.delete(entry.pathDigest)
      digestsLoaded.add(entry.pathDigest)
    }

    return debouncedProcessQueue()
  }

  return {
    startQueue: (options: {autoLoadAllDiffs?: boolean} = {autoLoadAllDiffs: false}) => {
      if (queueRunning) return
      queueRunning = true
      autoLoadAllDiffs = options.autoLoadAllDiffs ?? false
      debouncedProcessQueue()
    },
    loadPriority: (paths: string[]) => {
      digestsToLoad.pushFrontMany(paths)
      lengthOfItemsToLoad = paths.length
      // Debounce: reset the timer so we batch multiple rapid calls
      debouncedProcessQueue()
    },
    loadMore: (pathDigestOrEntry: string | {pathDigest: string}, _options?: {preferAnySoloEntry?: boolean}) => {
      // Support both new API (pathDigest string) and deprecated API (ProgressiveDiffEntry object)
      const pathDigest = typeof pathDigestOrEntry === 'string' ? pathDigestOrEntry : pathDigestOrEntry.pathDigest
      digestsToLoad.pushBack(pathDigest)
      // Debounce: reset the timer so we batch multiple rapid calls
      debouncedProcessQueue()
    },
  }
}
